'use client'

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { TRANSLATIONS, LOCALES, type Locale, type Translations } from './translations'

const STORAGE_KEY = 'locale'

function detectBrowserLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en'
  const lang = navigator.language.split('-')[0]
  return LOCALES.some(l => l.value === lang) ? (lang as Locale) : 'en'
}

interface LocaleContextValue {
  locale: Locale
  setLocale: (l: Locale) => void
  t: Translations
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: 'en',
  setLocale: () => {},
  t: TRANSLATIONS.en,
})

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('en')

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as Locale | null
    const effective = stored && LOCALES.some(l => l.value === stored) ? stored : detectBrowserLocale()
    setLocaleState(effective)
  }, [])

  function setLocale(l: Locale) {
    setLocaleState(l)
    localStorage.setItem(STORAGE_KEY, l)
  }

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t: TRANSLATIONS[locale] }}>
      {children}
    </LocaleContext.Provider>
  )
}

export function useLocale() {
  return useContext(LocaleContext)
}
